import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Handshake } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { PARTNER_TYPES } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";

const selCls = "border border-tertiary bg-white px-3 py-2.5 text-sm outline-none focus:border-champagne";

export default function PartnersList() {
  const { lang, L } = useLang();
  useSeo({
    title: lang === "fr" ? "Partenaires" : "Partners",
    description: lang === "fr"
      ? "Distributeurs, représentants, fabricants, fournisseurs et partenaires industriels qualifiés par RSTI."
      : "Distributors, representatives, manufacturers, suppliers and industrial partners qualified by RSTI.",
    lang,
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tax, setTax] = useState({ sector: [], market: [] });
  const [f, setF] = useState({ q: "", partner_type: "", sector: "", country: "", verification: "" });

  useEffect(() => {
    Promise.all([api.get("/taxonomies", { params: { type: "sector" } }), api.get("/taxonomies", { params: { type: "market" } })])
      .then(([s, m]) => setTax({ sector: s.data, market: m.data })).catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/partners", { params }).then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, [f]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <section className="border-b border-tertiary bg-sand">
        <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-16">
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI</span>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-anthracite sm:text-5xl">{lang === "fr" ? "Partenaires" : "Partners"}</h1>
          <p className="mt-3 max-w-2xl text-anthracite/70">{lang === "fr"
            ? "Distributeurs, représentants, agents commerciaux, fabricants, fournisseurs et partenaires — identifiés, vérifiés et qualifiés par RSTI."
            : "Distributors, representatives, commercial agents, manufacturers, suppliers and partners — identified, verified and qualified by RSTI."}</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="partner-types">
            {PARTNER_TYPES.map((o) => (
              <button key={o.value} onClick={() => set("partner_type", f.partner_type === o.value ? "" : o.value)} className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${f.partner_type === o.value ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary bg-white text-anthracite/70 hover:border-champagne"}`}>{o[lang]}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="mb-8 border border-tertiary bg-white p-4">
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-anthracite/40" />
            <input data-testid="partner-search" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder={lang === "fr" ? "Rechercher un partenaire…" : "Search a partner…"} className="w-full border border-tertiary bg-white py-2.5 pl-10 pr-3 text-sm outline-none focus:border-champagne" />
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="filter-partner-type" value={f.partner_type} onChange={(e) => set("partner_type", e.target.value)} className={selCls}><option value="">{lang === "fr" ? "Type" : "Type"}</option>{PARTNER_TYPES.map((o) => <option key={o.value} value={o.value}>{o[lang]}</option>)}</select>
            <select value={f.sector} onChange={(e) => set("sector", e.target.value)} className={selCls}><option value="">{lang === "fr" ? "Secteur" : "Sector"}</option>{tax.sector.map((s) => <option key={s.slug} value={s.slug}>{s[`label_${lang}`]}</option>)}</select>
            <select value={f.country} onChange={(e) => set("country", e.target.value)} className={selCls}><option value="">{lang === "fr" ? "Marché" : "Market"}</option>{tax.market.map((s) => <option key={s.slug} value={s.slug}>{s[`label_${lang}`]}</option>)}</select>
          </div>
        </div>

        {loading ? <p className="py-16 text-center text-anthracite/40">…</p> : items.length === 0 ? (
          <div className="border border-dashed border-tertiary bg-white py-20 text-center" data-testid="partner-empty">
            <Handshake className="mx-auto mb-3 h-6 w-6 text-anthracite/30" />
            <p className="text-anthracite/50">{lang === "fr" ? "Aucun partenaire publié pour ces critères." : "No published partner for these criteria."}</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((o) => (
              <Link key={o.id} to={`/partenaires/${o.slug}`} data-testid={`partner-card-${o.slug}`} className="group flex flex-col border border-tertiary bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                <div className="flex flex-1 flex-col p-6">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-champagne">{PARTNER_TYPES.find((x) => x.value === o.partner_type)?.[lang]}</span>
                    <VerifiedBadge verified={o.rst_verified} />
                  </div>
                  <div className="mb-3 flex items-center gap-3">
                    {o.logo ? <img src={o.logo} alt="" className="h-10 w-10 shrink-0 rounded border border-tertiary object-contain" /> : <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded border border-tertiary bg-sand"><Handshake className="h-4 w-4 text-anthracite/30" /></div>}
                    <h3 className="font-serif text-xl leading-snug text-anthracite">{o.organization}</h3>
                  </div>
                  <p className="mb-4 flex-1 text-sm leading-relaxed text-anthracite/70 line-clamp-3">{L(o, "summary")}</p>
                  <div className="flex items-center gap-1.5 border-t border-tertiary pt-4 text-xs text-anthracite/50"><MapPin className="h-3.5 w-3.5" />{[o.sector, o.country].filter(Boolean).join(" · ") || "—"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
